import { useMemo, type ReactElement } from 'react'
import { Vector3 } from 'three'
import { useAppStore } from '@stores'
import { formatTwoDecimals } from 'src/utils/numberFormatters'
import EnvelopeExtremeLabel from './EnvelopeExtremeLabel'
import {
  ENVELOPE_LABEL_CURVE_GAP,
  getExtremeLabelSpacing,
  stackExtremeLabels,
  type EnvelopeExtreme,
} from './utils'

interface Props {
  extreme: EnvelopeExtreme
  unit: string
  color: string
  textXDirection: Vector3
  textYDirection: Vector3
}

/**
 * Value labels of an envelope extreme, `ENVELOPE_LABEL_CURVE_GAP` away from the curve: one label,
 * or at a jump the left and right value as separate labels stacked along global z with the higher
 * value on top.
 */
const EnvelopeExtremeLabelStack = ({
  extreme,
  unit,
  color,
  textXDirection,
  textYDirection,
}: Props): ReactElement => {
  const textScale = useAppStore(state => state.loadRenderConfig.lineLoads.textScale)

  const labels = useMemo(
    () =>
      stackExtremeLabels(
        extreme,
        getExtremeLabelSpacing(textScale),
        textYDirection,
        ENVELOPE_LABEL_CURVE_GAP,
      ),
    [extreme, textScale, textYDirection],
  )

  return (
    <>
      {labels.map(({ value, position }) => (
        <EnvelopeExtremeLabel
          key={value}
          position={position}
          isAboveAxis={extreme.isAboveAxis}
          text={`${formatTwoDecimals(value)} ${unit}`}
          color={color}
          scale={textScale}
          textXDirection={textXDirection}
          textYDirection={textYDirection}
        />
      ))}
    </>
  )
}

export default EnvelopeExtremeLabelStack
